import { useMutation } from '@tanstack/react-query';
import JSONBig from 'json-bigint';
import { axiosClient } from '../axiosClient';
import { CoordinatesType } from '@/types/dataTypes';
import { NormalizedIndexResponseType } from '@/types/apiTypes';

interface PointIndexType {
  id: string;
  name: string;
  fullName: string;
  homeTypes: string[];
}

// The API has no "all indexes covering a point" endpoint, so this resolves the
// best index per home type and describes each via the normalized endpoint
const HOME_TYPES = ['apartment', 'house'];

const fetchBestIndexId = async (
  homeType: string,
  coordinates: CoordinatesType
): Promise<string | null> => {
  if (coordinates.lat === null || coordinates.lng === null) return null;

  const parameters = new URLSearchParams({
    home_type: homeType,
    wgs84lat: coordinates.lat.toString(),
    wgs84lon: coordinates.lng.toString(),
  }).toString();

  const { data } = await axiosClient.get<{ index_id: string }>(
    `${import.meta.env.VITE_API_INDEX_BEST_URL}?${parameters}`,
    {
      transformResponse: [
        (raw) => {
          const parsed = JSONBig({ storeAsString: true }).parse(raw);
          return { index_id: parsed.index_id };
        },
      ],
    }
  );

  return data?.index_id ?? null;
};

const useIndexesAtPoint = () => {
  return useMutation({
    mutationFn: async (
      coordinates: CoordinatesType
    ): Promise<PointIndexType[]> => {
      const ids = await Promise.all(
        HOME_TYPES.map((homeType) => fetchBestIndexId(homeType, coordinates))
      );

      const homeTypesById = new Map<string, string[]>();
      HOME_TYPES.forEach((homeType, position) => {
        const id = ids[position];
        if (!id) return;
        homeTypesById.set(id, [...(homeTypesById.get(id) ?? []), homeType]);
      });

      return Promise.all(
        Array.from(homeTypesById.entries()).map(async ([id, homeTypes]) => {
          const { data } = await axiosClient.get<NormalizedIndexResponseType>(
            `${import.meta.env.VITE_API_INDEX_NORMALIZED_URL}?id=${id}`
          );

          return {
            id,
            name: data?.short_name || id,
            fullName: data?.full_name || '',
            homeTypes,
          };
        })
      );
    },
  });
};

export default useIndexesAtPoint;
export type { PointIndexType };
